import { Component, Inject, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, NgModel } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Consultant, ConsultantSalary, POSITION_DEFAULTS, Position } from 'src/app/models/consultant.model';
import { Department } from 'src/app/models/department.model';
import { ConsultantService } from 'src/app/services/consultant.service';
import { ConsultantSalaryService } from 'src/app/services/consultant-salary.service';
import { DepartmentService } from 'src/app/services/department.service';

@Component({
    selector: 'app-consultant',
    templateUrl: './consultant.component.html',
    styleUrls: ['./consultant.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConsultantComponent implements OnInit {
  loading = true;
  consultant: Consultant;
  salaries: ConsultantSalary[] = [];
  private removedSalaryIds: string[] = [];
  Position = Position;

  departments: Department[];
  departmentsDropdown = [];
  departmentControl: FormControl;
  consultantExists: boolean;

  @ViewChildren(NgModel) ngModels: QueryList<NgModel>;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ConsultantComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    protected consultants: ConsultantService,
    protected consultantSalaries: ConsultantSalaryService,
    protected departmentsService: DepartmentService,
  ) {
    this.consultant = data.consultant;
    this.consultant.salaries = this.consultant.salaries ?? [];
    this.salaries = this.consultant.salaries;

    this.departmentControl = this.fb.control(this.consultant.departmentId);

  }

  async ngOnInit(): Promise<void> {
    this.departments = await this.departmentsService.list();

    this.departmentsDropdown = this.departments.map(_ => {
      return { display: _.name, value: _.id };
    });

    this.departmentControl.valueChanges.subscribe(_ => {
      this.consultant.departmentId = _;
    });

    this.loading = false;
  }

  async checkExisting() {
    try {
      this.consultantExists = await this.consultants.exists(this.consultant.initials.toUpperCase());
    } catch (error) {
    }
  }

  async save() {
    if (this.ngModels.some(_ => _.invalid) || !this.valid()) {
      this.departmentControl.markAllAsTouched();
      this.ngModels.forEach(_ => _.control.markAllAsTouched());
    } else {
      try {
        this.consultant.initials = this.consultant.initials.toUpperCase();
        const saved = await this.consultants.save(this.consultant);
        this.consultant.id = saved.id;

        for (const id of this.removedSalaryIds) {
          await this.consultantSalaries.delete(id);
        }
        this.removedSalaryIds = [];

        for (const salary of this.salaries) {
          salary.consultantId = this.consultant.id;
          const saved = await this.consultantSalaries.save(salary);
          salary.id = saved.id;
        }

        this.dialogRef.close(this.consultant);
      } catch (error) {
        console.error(error);
      }
    }


  }

  valid() {
    if (!this.consultant.name || !this.consultant.initials || !this.consultant.email || !this.consultant.departmentId)
      return false;

    if (!this.consultant.id && this.consultantExists)
      return false;

    return true;
  }

  addPersonalSalesBudget() {
    this.consultant.budgetPersonalSales.push({ year: new Date().getFullYear(), amount: null });
  }

  addTotalSalesBudget() {
    this.consultant.budgetTotalSales.push({ year: new Date().getFullYear(), amount: null });
  }

  deletePersonalSalesBudget(idx: number) {
    this.consultant.budgetPersonalSales.splice(idx, 1);
  }

  deleteTotalSalesBudget(idx: number) {
    this.consultant.budgetTotalSales.splice(idx, 1);
  }

  addSalary() {
    const mostRecent = this.salaries.length > 0
      ? this.salaries.reduce((a, b) => (a.year ?? 0) > (b.year ?? 0) ? a : b)
      : null;
    this.salaries.push(new ConsultantSalary(
      this.consultant.id,
      new Date().getFullYear(),
      mostRecent?.monthlySalary ?? null,
      mostRecent?.monthlySharedExpenses ?? null,
      mostRecent?.position ?? Position.None,
      mostRecent?.bonusPercentage ?? 0,
    ));
  }

  deleteSalary(idx: number) {
    const [removed] = this.salaries.splice(idx, 1);
    if (removed?.id) {
      this.removedSalaryIds.push(removed.id);
    }
  }

  onPositionChange(salary: ConsultantSalary, newPosition: Position) {
    salary.position = newPosition;
    salary.bonusPercentage = POSITION_DEFAULTS[newPosition];
  }
}
